/**
 * Zone membership on a draft: every object is in exactly one zone list, and its `zone` field names it
 * (fuzz invariant, docs/design/08 §2.2). Battlefield cards are referenced by `BattlefieldState.card`;
 * chain objects by their `ChainItem.object`.
 */
import { invariant } from '../../kernel/errors';
import type { GameState } from '../state/game-state';
import type { ObjectId, PlayerId } from '../state/ids';
import type { GameObject } from '../state/objects';
import { type DeckPosition, PLAYER_ZONE_KINDS, type ZoneRef } from '../state/zones';
import { getBattlefield } from './queries';

/**
 * The mutable id list of a zone. The chain has no plain list (it holds `ChainItem`s), so it is not
 * accepted here; battlefield cards are not in `permanents` either.
 */
export function zoneList(draft: GameState, zone: Exclude<ZoneRef, { kind: 'chain' }>): ObjectId[] {
  switch (zone.kind) {
    case 'battlefield':
      return getBattlefield(draft, zone.battlefield).permanents;
    case 'facedown':
      return getBattlefield(draft, zone.battlefield).facedown;
    case 'mainDeck':
    case 'runeDeck':
    case 'hand':
    case 'trash':
    case 'banishment':
    case 'champion':
    case 'legend':
    case 'base':
    case 'runes': {
      const zones = draft.zones[zone.player];
      invariant(zones, `No zones for ${zone.player}`);
      return zones[zone.kind];
    }
  }
}

/** Removes `object` from the list (or chain item) that holds it. */
export function detachFromZone(draft: GameState, object: GameObject): void {
  if (object.zone.kind === 'chain') {
    const index = draft.chain.findIndex((item) => item.object === object.id);
    invariant(index >= 0, `Object ${object.id} is not on the chain`);
    draft.chain.splice(index, 1);
    return;
  }
  if (object.category === 'battlefield') invariant(false, 'Battlefield cards never leave their location');
  const list = zoneList(draft, object.zone);
  const index = list.indexOf(object.id);
  invariant(index >= 0, `Object ${object.id} is not in its zone list`);
  list.splice(index, 1);
}

/** Inserts `id` into a zone list. Positions only matter in decks; elsewhere the object goes last. */
export function insertIntoList(
  list: ObjectId[],
  id: ObjectId,
  position: DeckPosition | undefined,
  ordered: boolean,
): void {
  if (!ordered || position === undefined || position === 'bottom') {
    list.push(id);
    return;
  }
  const index = position === 'top' ? 0 : Math.max(0, Math.min(list.length, Math.trunc(position)));
  list.splice(index, 0, id);
}

/** Every `(zone list owner, id)` pair, used by invariant checks: lists, battlefield cards and chain objects. */
export function allZoneMemberships(state: GameState): { id: ObjectId; zone: ZoneRef }[] {
  const out: { id: ObjectId; zone: ZoneRef }[] = [];
  for (const player of Object.keys(state.zones) as PlayerId[]) {
    const zones = state.zones[player];
    if (!zones) continue;
    for (const kind of PLAYER_ZONE_KINDS) {
      for (const id of zones[kind]) out.push({ id, zone: { kind, player } });
    }
  }
  for (const bf of state.battlefields) {
    out.push({ id: bf.card, zone: { kind: 'battlefield', battlefield: bf.id } });
    for (const id of bf.permanents) out.push({ id, zone: { kind: 'battlefield', battlefield: bf.id } });
    for (const id of bf.facedown) out.push({ id, zone: { kind: 'facedown', battlefield: bf.id } });
  }
  for (const item of state.chain) if (item.object !== null) out.push({ id: item.object, zone: { kind: 'chain' } });
  return out;
}
